import { sdk } from '@needful-things-mvp/sdk';

import { hoursSince } from './api-format';
import { USE_MOCK } from './data-mode';
import { unwrap } from './http';
import { type Paginated, delay, paginate, slugify } from './mock';

export type SubscriberSource = 'jury' | 'post_vote';

export interface Subscriber {
  id: string;
  name: string | null;
  email: string;
  source: SubscriberSource;
  project: string | null;
  projectSlug: string | null;
  date: string;
  updatedHours: number;
}

export interface ListSubscribersParams {
  search?: string;
  source?: 'all' | SubscriberSource;
  page?: number;
  pageSize?: number;
}

export type PaginatedSubscribers = Paginated<Subscriber>;

export const SUBSCRIBER_PAGE_SIZE = 8;
export const SUBSCRIBER_PAGE_SIZE_OPTIONS = [8, 16, 24, 48];

let db: Subscriber[] = [
  {
    id: 'sub-1',
    name: 'Ana Souza',
    email: 'ana.souza@example.com',
    source: 'jury',
    project: null,
    projectSlug: null,
    date: '02 Oct 2026',
    updatedHours: 4,
  },
  {
    id: 'sub-2',
    name: 'Bruno Lima',
    email: 'bruno.lima@example.com',
    source: 'jury',
    project: null,
    projectSlug: null,
    date: '03 Oct 2026',
    updatedHours: 6,
  },
  {
    id: 'sub-3',
    name: 'Carla Dias',
    email: 'carla.dias@example.com',
    source: 'post_vote',
    project: 'Bloom',
    projectSlug: 'bloom',
    date: '07 Oct 2026',
    updatedHours: 20,
  },
  {
    id: 'sub-4',
    name: null,
    email: 'diego.rocha@example.com',
    source: 'post_vote',
    project: 'Bloom',
    projectSlug: 'bloom',
    date: '08 Oct 2026',
    updatedHours: 30,
  },
  {
    id: 'sub-5',
    name: 'Elisa Prado',
    email: 'elisa.prado@example.com',
    source: 'jury',
    project: null,
    projectSlug: null,
    date: '09 Oct 2026',
    updatedHours: 40,
  },
  {
    id: 'sub-6',
    name: 'Felipe Nunes',
    email: 'felipe.nunes@example.com',
    source: 'post_vote',
    project: 'Salt & Iron',
    projectSlug: 'salt-and-iron',
    date: '12 Oct 2026',
    updatedHours: 60,
  },
  {
    id: 'sub-7',
    name: 'Gina Park',
    email: 'gina.park@example.com',
    source: 'jury',
    project: null,
    projectSlug: null,
    date: '14 Oct 2026',
    updatedHours: 90,
  },
  {
    id: 'sub-8',
    name: 'Hugo Meyer',
    email: 'hugo.meyer@example.com',
    source: 'post_vote',
    project: 'Ember Field',
    projectSlug: 'ember-field',
    date: '18 Oct 2026',
    updatedHours: 120,
  },
  {
    id: 'sub-9',
    name: 'Iris Cole',
    email: 'iris.cole@example.com',
    source: 'jury',
    project: null,
    projectSlug: null,
    date: '20 Oct 2026',
    updatedHours: 150,
  },
  {
    id: 'sub-10',
    name: 'Jonas Vidal',
    email: 'jonas.vidal@example.com',
    source: 'jury',
    project: null,
    projectSlug: null,
    date: '22 Oct 2026',
    updatedHours: 180,
  },
];

/** Wire shape from `GET /admin/subscribers` (project resolved by the API). */
interface SubscriberDto {
  id: string;
  name: string | null;
  email: string;
  source: SubscriberSource;
  project: string | null;
  projectSlug: string | null;
  createdAt: string;
}

function mapSubscriber(dto: SubscriberDto): Subscriber {
  return {
    id: dto.id,
    name: dto.name,
    email: dto.email,
    source: dto.source,
    project: dto.project,
    projectSlug: dto.projectSlug,
    date: new Date(dto.createdAt).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }),
    updatedHours: hoursSince(dto.createdAt),
  };
}

export async function listSubscribers(
  params: ListSubscribersParams = {},
): Promise<PaginatedSubscribers> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.subscribers.get({
      query: {
        source: params.source && params.source !== 'all' ? params.source : undefined,
        search: params.search,
        page: params.page,
        pageSize: params.pageSize,
      },
    });
    const page = unwrap<Paginated<SubscriberDto>>(response);
    return { ...page, items: page.items.map(mapSubscriber) };
  }

  await delay();
  const { search, source = 'all', page, pageSize = SUBSCRIBER_PAGE_SIZE } = params;

  const term = search?.trim().toLowerCase();
  const filtered = db.filter((subscriber) => {
    const matchesSource = source === 'all' || subscriber.source === source;
    const matchesSearch =
      !term ||
      subscriber.email.toLowerCase().includes(term) ||
      (subscriber.name ?? '').toLowerCase().includes(term);
    return matchesSource && matchesSearch;
  });

  const sorted = [...filtered].sort((a, b) => a.updatedHours - b.updatedHours);
  return paginate(sorted, page, pageSize);
}

export function querySubscribers(
  params: ListSubscribersParams = {},
): Promise<PaginatedSubscribers> {
  return listSubscribers(params);
}

export async function createSubscriber(input: {
  email: string;
  name?: string;
  source?: SubscriberSource;
  project?: string;
}): Promise<Subscriber> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.subscribers.post({
      email: input.email,
      name: input.name,
      source: input.source,
      projectId: input.project,
    });
    return mapSubscriber(unwrap<SubscriberDto>(response));
  }
  await delay();
  const existing = db.find((subscriber) => subscriber.email === input.email);
  if (existing) return existing;

  const project = input.project?.trim() || null;
  const subscriber: Subscriber = {
    id: crypto.randomUUID(),
    name: input.name ?? null,
    email: input.email,
    source: input.source ?? 'jury',
    project,
    projectSlug: project ? slugify(project) : null,
    date: new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }),
    updatedHours: 0,
  };
  db = [subscriber, ...db];
  return subscriber;
}

export async function deleteSubscriber(id: string): Promise<void> {
  if (!USE_MOCK) {
    await sdk.api.admin.subscribers({ id }).delete();
    return;
  }
  await delay();
  db = db.filter((subscriber) => subscriber.id !== id);
}
